/**
 * F-099 / F-050 (slice 7) — the parts Sales lists per product on the Modify Existing Product
 * form → the wire (`products[i].parts`, the New Product `spec.sections[*]` shape + optional
 * modifiers + the PIM sourcing Part 1 keeps). Nothing is required: a part row with nothing in it
 * is dropped, a modifier row without a complete input is dropped, and a product Sales never
 * touched sends no list at all (D&D then seeds Part 1 from PIM at intake, as before).
 */
import { findSubstrate } from '@/data/mockSignBuilderCatalog'
import { modifierFromDraft, modifierText } from '@/lib/dd-queue/lineParts'
import { productPartsKey } from '@/lib/sales-workflow/pimPartDraft'
import type { WorkflowPimProduct } from '@/lib/sales-workflow/workflowPimProduct'
import type { OriginModifyPart, OriginPartModifier, OriginSubstrateRef } from '@/types/origin'
import {
  MODIFY_PART_CURRENT_SUBSTRATE,
  type ModifyFormData,
  type ModifyPartDraft,
  type ModifyPartModifierDraft
} from '@/types/salesWorkflow'

export const MODIFY_PARTS_MAX = 20

function trimOrNull(value: string): string | null {
  const text = value.trim()
  return text === '' ? null : text
}

function positiveNumber(raw: string): number | null {
  const text = raw.trim()
  if (text === '') return null
  const n = Number(text)
  return Number.isFinite(n) && n > 0 ? n : null
}

function pieces(raw: string): number {
  const n = Number(raw.trim())
  return Number.isInteger(n) && n >= 1 ? n : 1
}

/** The substrate of a part: a catalog pick (id + PIQ code), or PIM's current stock as a label-only ref. */
export function modifyPartSubstrateRef(draft: ModifyPartDraft): OriginSubstrateRef | null {
  const id = draft.substrate.trim()
  if (id === MODIFY_PART_CURRENT_SUBSTRATE) {
    const label = draft.current_substrate.trim()
    return label ? { id: '', piq: '', label } : null
  }
  if (id === '') return null
  const sub = findSubstrate(id)
  return sub ? { id: sub.id, piq: sub.piq, label: sub.label, matType: sub.matType } : null
}

/** Modifier rows → wire; incomplete rows are dropped (Sales may leave them half-filled). */
export function modifyPartModifiersToWire(
  modifiers: readonly ModifyPartModifierDraft[]
): OriginPartModifier[] {
  const out: OriginPartModifier[] = []
  modifiers.forEach((m, i) => {
    const issues: string[] = []
    const wire = modifierFromDraft(m, 'Part', i + 1, issues)
    if (wire && issues.length === 0) out.push(wire)
  })
  return out
}

/** A part row Sales left empty — not sent. */
export function isEmptyModifyPart(draft: ModifyPartDraft): boolean {
  return (
    draft.label.trim() === '' &&
    !draft.vended &&
    draft.substrate.trim() === '' &&
    draft.width.trim() === '' &&
    draft.height.trim() === '' &&
    draft.notes.trim() === '' &&
    modifyPartModifiersToWire(draft.modifiers).length === 0
  )
}

/**
 * The sourcing preset a part keeps: hardware stays hardware whatever the mark says (the mark
 * only moves it between in-house and drop-shipped); anything else is vended when marked, else
 * the manufactured default (null → no key on the wire).
 */
export function modifyPartSourcing(draft: ModifyPartDraft): string | null {
  const sourcing = draft.sourcing.trim()
  const hardware = sourcing === 'hardware_in_house' || sourcing === 'hardware_drop_shipped'
  if (draft.vended) return hardware ? 'hardware_drop_shipped' : 'vended_drop_shipped'
  return hardware ? 'hardware_in_house' : null
}

export function modifyPartToWire(draft: ModifyPartDraft): OriginModifyPart {
  const vended = draft.vended === true
  const modifiers = modifyPartModifiersToWire(draft.modifiers)
  const sourcing = modifyPartSourcing(draft)
  return {
    label: draft.label.trim(),
    substrate: vended ? null : modifyPartSubstrateRef(draft),
    width: vended ? null : positiveNumber(draft.width),
    height: vended ? null : positiveNumber(draft.height),
    pieces: pieces(draft.pieces),
    single_double: draft.single_double || 'single',
    notes: trimOrNull(draft.notes),
    ...(vended ? { vended: true } : {}),
    ...(sourcing && sourcing !== 'manufactured_in_house' ? { sourcing } : {}),
    ...(modifiers.length > 0 ? { modifiers } : {})
  }
}

/** The list to send for a product: null when Sales never touched it or left nothing in it. */
export function modifyPartsForProduct(
  formData: Pick<ModifyFormData, 'parts_by_product' | 'parts_touched'>,
  product: Pick<WorkflowPimProduct, 'id' | 'sku'>
): OriginModifyPart[] | null {
  const key = productPartsKey(product)
  if (formData.parts_touched[key] !== true) return null
  const drafts = (formData.parts_by_product[key] ?? []).filter((d) => !isEmptyModifyPart(d))
  if (drafts.length === 0) return null
  return drafts.slice(0, MODIFY_PARTS_MAX).map(modifyPartToWire)
}

export function hasModifyParts(
  formData: Pick<ModifyFormData, 'parts_by_product' | 'parts_touched'>,
  products: readonly Pick<WorkflowPimProduct, 'id' | 'sku'>[]
): boolean {
  return products.some((p) => modifyPartsForProduct(formData, p) !== null)
}

/** "Part 2 (Backer): Coroplast 4mm White · 24" × 36" · ×2 · Double-sided · Modifiers: GROM 4 CNR ×4" — Review / detail / D&D card. */
export function modifyPartText(part: OriginModifyPart, index: number): string {
  const label = part.label.trim()
  const head = label ? `Part ${index + 1} (${label})` : `Part ${index + 1}`
  const bits: string[] = []
  if (part.vended) bits.push('Vended (drop-shipped)')
  else {
    if (part.substrate?.label) bits.push(part.substrate.label)
    const w = part.width != null ? `${part.width}"` : ''
    const h = part.height != null ? `${part.height}"` : ''
    if (w && h) bits.push(`${w} × ${h}`)
    else if (w || h) bits.push(w ? `W ${w}` : `H ${h}`)
  }
  if (part.pieces && part.pieces !== 1) bits.push(`×${part.pieces}`)
  if (part.single_double === 'double') bits.push('Double-sided')
  if (part.modifiers && part.modifiers.length > 0) {
    bits.push(`Modifiers: ${part.modifiers.map((m) => modifierText(m)).join(', ')}`)
  }
  if (part.notes) bits.push(`Notes: ${part.notes}`)
  return bits.length ? `${head}: ${bits.join(' · ')}` : head
}
